// ---------------------------------------------------------------------------
// BYOD dashboard constants
// ---------------------------------------------------------------------------

/** Maximum non-section widgets allowed per dashboard. */
export const MAX_WIDGETS = 20;

/** Maximum tiles per row, keyed by row type. */
export const MAX_TILES_PER_ROW: Record<string, number> = {kpi: 4, viz: 2};

/** Number of placements per batch in the initial query. */
export const QUERY_BATCH_SIZE = 5;

/** Default number of rows shown per table page. */
export const DEFAULT_PAGE_SIZE = 10;

/** Filter types excluded from widget filter sidebars (handled by date range picker). */
export const DATE_FILTER_TYPES = new Set([
  'date_range_start',
  'date_range_end',
]);

/** Filter key for the time-series grouping frequency (hour/day/week/…). */
export const FREQUENCY_FILTER_KEY = 'frequency';

/**
 * Filters that must always carry a value even when the user hasn't picked one.
 * Unlike ordinary filters — which are optional, so an empty selection means
 * "show all" — `frequency` is a required grouping dimension for time-series
 * widgets: the query is invalid without it. Keys listed here are seeded to
 * their first static option; every other filter is left empty.
 */
export const REQUIRED_DEFAULT_FILTER_KEYS: ReadonlySet<string> = new Set([
  FREQUENCY_FILTER_KEY,
]);

// ---------------------------------------------------------------------------
// Special-cased structured filters
// ---------------------------------------------------------------------------
// These filter keys render a dedicated structured picker — see
// WidgetFilterSidebar (components/widget-filter-sidebar), which renders each in
// its own CollapsibleCard — instead of the generic ByodFilterField, and carry a
// structured value (see ByodFilterValue in src/types/byod.ts) rather than a
// plain string[].

/** Filter key for the hierarchical workflow→node picker. */
export const WORKFLOW_NODE_MAPPING_KEY = 'workflow_node_mappings';

/**
 * Filter key for the entity (type → attribute → value) picker. Matches how the
 * analytics platform persists this dimension elsewhere (`entity_data_filter`).
 */
export const ENTITY_FILTER_KEY = 'entity_data_filter';

/**
 * Keys whose selection value is structured, not a plain `string[]`. The
 * `string[]`-assuming hops in the filter pipeline (getActiveFilters, the
 * edit/preview init coercion) must pass these through untouched.
 */
export const SPECIAL_FILTER_KEYS: ReadonlySet<string> = new Set([
  WORKFLOW_NODE_MAPPING_KEY,
  ENTITY_FILTER_KEY,
]);
/**
 * URL query param that holds the active dashboard date range. Written/read by
 * `useByodDateRange` (via `useQueryObject`) and carried forward across every
 * builder ↔ panel navigation by `buildByodPanelQuery` so the selected range
 * survives while the user stays inside the dashboard.
 */
export const BYOD_DATE_RANGE_QUERY_KEY = 'byod_date_range';

/**
 * `widget_config` key that opts a KPI into a comparison-period trend. When set
 * (`{ show_trend: true }`) it's forwarded to `/preview` and `/query`, and the
 * backend responds with a nested `data: { value, comparison_value,
 * trend_percentage, trend_direction }` (flattened by `normalizePreviewResponse`).
 * When false/absent the backend returns just the flat `value`. The "Show trend"
 * toggle is offered on every KPI widget.
 */
export const SHOW_TREND_CONFIG_KEY = 'show_trend';

/**
 * `widget_config` key holding a KPI's chosen semantic (`neutral` | `success` |
 * `information` | `warning` | `danger`). It tints the KPIBox icon accent only
 * and is a pure display concern — like `chart_style`, it's stripped from the
 * `/preview` and `/query` payloads but persisted in `widget_config_json` so the
 * choice round-trips. Absent ⇒ `neutral`. The picker is offered on every KPI
 * widget.
 */
export const KPI_SEMANTIC_CONFIG_KEY = 'kpi_semantic';
